import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Calendar, Clock, Crown, Sparkles, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface TrialStatusCardProps {
  sessionToken: string;
  userEmail: string;
  onStatusUpdate?: (status: SubscriptionStatus | null) => void;
}

interface SubscriptionStatus {
  subscription_status: 'trial' | 'active' | 'expired' | 'cancelled';
  trial_end_date: string | null;
  subscription_end_date: string | null;
  payment_frequency: 'monthly' | 'yearly' | null;
  subscribed?: boolean;
}

export const TrialStatusCard = ({ sessionToken, userEmail, onStatusUpdate }: TrialStatusCardProps) => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const fetchStatus = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        body: { email: userEmail, session_token: sessionToken }
      });

      if (error) throw error;
      setStatus(data);
      onStatusUpdate?.(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      toast({
        title: t('trialStatus.errors.checkStatusError'),
        description: t('trialStatus.errors.checkStatusErrorDesc'),
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [sessionToken, userEmail]);

  const calculateTrialProgress = () => {
    if (!status?.trial_end_date) return 0;
    
    const now = new Date();
    const trialEnd = new Date(status.trial_end_date);
    const trialStart = new Date(trialEnd.getTime() - (5 * 24 * 60 * 60 * 1000)); // 5 days ago
    
    const totalDuration = trialEnd.getTime() - trialStart.getTime();
    const elapsed = now.getTime() - trialStart.getTime();
    
    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  const getDaysRemaining = () => {
    if (!status?.trial_end_date) return 0;
    
    const now = new Date();
    const trialEnd = new Date(status.trial_end_date);
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(diffDays, 0);
  };

  const getTimeRemaining = () => {
    if (!status?.trial_end_date) return '';
    
    const now = new Date();
    const trialEnd = new Date(status.trial_end_date);
    const diffTime = trialEnd.getTime() - now.getTime();
    
    if (diffTime <= 0) return t('trialStatus.info.expired');
    
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    // Show hours when 2 days or less remaining
    if (days <= 2 && days >= 0) {
      if (days === 0) {
        return `${hours}h`;
      } else {
        return `${days}d ${hours}h`;
      }
    }
    
    return `${days} ${days === 1 ? t('trialStatus.info.day') : t('trialStatus.info.days')}`;
  };

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-muted rounded w-1/2"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  const isTrialActive = status.subscription_status === 'trial';
  const isSubscribed = status.subscription_status === 'active';
  const isExpired = status.subscription_status === 'expired';
  const isCancelled = status.subscription_status === 'cancelled';
  
  const daysRemaining = getDaysRemaining();
  const trialProgress = calculateTrialProgress();

  return (
    <Card className={`relative overflow-hidden ${isSubscribed ? 'border-newsletter-primary/50' : isTrialActive ? 'border-newsletter-accent/50' : 'border-destructive/50'}`}>
      {/* Background effects */}
      {isSubscribed && (
        <div className="absolute inset-0 bg-newsletter-gradient opacity-5"></div>
      )}
      
      <CardHeader className="relative z-10 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {isSubscribed ? (
              <>
                <Crown className="w-5 h-5 text-newsletter-primary" />
                {t('trialStatus.titles.subscriptionStatus')}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 text-newsletter-accent" />
                {t('trialStatus.titles.trialStatus')}
              </>
            )}
          </CardTitle>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchStatus}
            disabled={isRefreshing}
            className="text-xs"
          >
            {isRefreshing ? t('trialStatus.actions.refreshing') : t('trialStatus.actions.refresh')}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge 
            variant={isSubscribed ? 'default' : isTrialActive ? 'secondary' : 'destructive'}
            className={isSubscribed ? 'bg-newsletter-primary' : isTrialActive ? 'bg-newsletter-accent' : ''}
          >
            {isSubscribed ? t('trialStatus.badges.premiumSubscriber') : 
             isTrialActive ? t('trialStatus.badges.activeTrial') :
             isExpired ? t('trialStatus.badges.expiredTrial') :
             isCancelled ? t('trialStatus.badges.cancelled') : t('trialStatus.badges.unknownStatus')}
          </Badge>
          
          {status.payment_frequency && (
            <Badge variant="outline" className="text-xs">
              {status.payment_frequency === 'monthly' ? t('trialStatus.badges.monthly') : t('trialStatus.badges.yearly')}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative z-10 space-y-4">
        {/* Trial Status */}
        {isTrialActive && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('trialStatus.info.daysRemaining')}
              </span>
              <span className="font-medium text-newsletter-accent">
                {getTimeRemaining()}
              </span>
            </div>
            
            <div className="space-y-2">
              <Progress 
                value={trialProgress} 
                className="h-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('trialStatus.info.trialStarted')}</span>
                <span>{t('trialStatus.info.expiresOn')} {formatDate(status.trial_end_date!)}</span>
              </div>
            </div>

            {daysRemaining <= 2 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-newsletter-accent/10 border border-newsletter-accent/20">
                <AlertCircle className="w-4 h-4 text-newsletter-accent flex-shrink-0 mt-0.5" />
                <div className="space-y-2 flex-1">
                  <p className="text-sm font-medium">{t('trialStatus.warnings.trialEnding')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('trialStatus.warnings.trialEndingDesc')}
                  </p>
              <Link to="/checkout">
                <Button size="sm" className="bg-newsletter-gradient hover:opacity-90 text-white">
                  {t('trialStatus.actions.subscribeNow')}
                </Button>
              </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscription Status */}
        {isSubscribed && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('trialStatus.info.nextRenewal')}
              </span>
              <span className="font-medium">
                {status.subscription_end_date ? formatDate(status.subscription_end_date) : 'N/A'}
              </span>
            </div>
            
            <div className="p-3 rounded-lg bg-newsletter-primary/10 border border-newsletter-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-newsletter-primary" />
                <span className="text-sm font-medium">{t('trialStatus.info.premiumBenefitsActive')}</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• {t('trialStatus.benefits.0')}</li>
                <li>• {t('trialStatus.benefits.1')}</li>
                <li>• {t('trialStatus.benefits.2')}</li>
                <li>• {t('trialStatus.benefits.3')}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Expired/Cancelled Status */}
        {(isExpired || isCancelled) && (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-destructive" />
                <span className="text-sm font-medium">
                  {isExpired ? t('trialStatus.warnings.trialExpired') : t('trialStatus.warnings.subscriptionCancelled')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {isExpired 
                  ? t('trialStatus.warnings.expiredDesc')
                  : t('trialStatus.warnings.cancelledDesc')
                }
              </p>
              <Link to="/checkout">
                <Button size="sm" className="bg-newsletter-gradient hover:opacity-90 text-white">
                  {isExpired ? t('trialStatus.actions.subscribeNow') : t('trialStatus.actions.reactivateSubscription')}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Last updated info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('trialStatus.info.lastUpdate')} {new Date().toLocaleTimeString(i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};